import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { teamMembersApi, ticketEntriesApi, ticketingPartnersApi } from "../../../api";
import { rsvpApi } from "../../../api/rsvp";
import { walkInApi } from "../../../api/walkIn";
import { CurrencyInput } from "../../../components/CurrencyInput";
import { MobileSelect } from "../../../components/ui/MobileSelect";
import { QueryErrorBanner } from "../../../components/feedback/QueryFeedback";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { formatINR } from "../../../utils/format";
import { toE164 } from "../../../utils/phone";
import {
  downloadTicketEntriesCsv,
  fetchAllTicketEntries,
  ticketEntryExportFilename,
} from "../../../utils/ticketEntryCsv";
import { queryKeys } from "../../../queryKeys";
import type { TeamMember, LiveOpsTier, PaymentMode, TicketEntryLog, TicketingPartner } from "../../../types";
import type { RsvpGuest, TierCountMap, WalkInGuest } from "../../../types/rsvp";
import { normalizeGuestNumber, resolveGuestListLabel } from "../../../types/rsvp";
import { TicketEntryLogList } from "./TicketEntryLogList";
import {
  ENTRY_LOG_INITIAL_VISIBLE,
  ENTRY_LOG_PAGE_SIZE,
} from "./ticketEntryLogPagination";

const DEFAULT_PARTNER_SLUG = "zoniq";

type GateLookupGuest = {
  /** Prefixed key for React lists / dedupe (`rsvp:` | `walkin:`). */
  id: string;
  /** Raw rsvp_guests row id — RSVP, Guest List and Walk-in all live in that table. */
  guestId: string;
  name: string;
  phone: string;
  email: string | null;
  party_size: number;
  tier_counts: TierCountMap;
  amount: number | null;
  payment_mode?: PaymentMode | null;
  source: "RSVP" | "Guest List" | "Walk-in";
};

/** The tier the guest booked most of — used to preselect their tier at the gate. */
function dominantTierId(tierCounts: TierCountMap | undefined | null): string | null {
  const entries = Object.entries(tierCounts ?? {}).filter(([, n]) => Number(n) > 0);
  if (entries.length === 0) return null;
  return entries.sort((a, b) => Number(b[1]) - Number(a[1]))[0][0];
}

function mergeEntries(existing: TicketEntryLog[], incoming: TicketEntryLog[]): TicketEntryLog[] {
  const byId = new Map<string, TicketEntryLog>();
  [...existing, ...incoming].forEach((entry) => byId.set(entry.id, entry));
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );
}

function newestOccurredAt(entries: TicketEntryLog[]): string | undefined {
  if (entries.length === 0) return undefined;
  return entries.reduce((latest, entry) =>
    new Date(entry.occurred_at) > new Date(latest.occurred_at) ? entry : latest,
  ).occurred_at;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

type Props = {
  eventId: string;
  token: string;
  tiers: LiveOpsTier[];
};

export const LiveTicketEntriesPanel: React.FC<Props> = ({ eventId, token, tiers }) => {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<TicketEntryLog[]>([]);
  const [pollFrom, setPollFrom] = useState<string | undefined>(undefined);
  const [tierId, setTierId] = useState("");
  const [quantitySold, setQuantitySold] = useState("1");
  const [amountOverride, setAmountOverride] = useState("");
  // Tracks "the operator has started typing a custom amount" separately from
  // whether the field currently reads empty — otherwise backspacing the
  // value to clear it (a normal way to retype a number) made isAmountOverridden
  // go false and the field snap straight back to the auto amount, so it
  // looked impossible to actually change.
  const [amountTouched, setAmountTouched] = useState(false);
  const [partnerSelection, setPartnerSelection] = useState("");
  const [operatorId, setOperatorId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [visibleLogCount, setVisibleLogCount] = useState(ENTRY_LOG_INITIAL_VISIBLE);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [registeredNumber, setRegisteredNumber] = useState("");
  const [guestName, setGuestName] = useState("");
  const [showGuestResults, setShowGuestResults] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<GateLookupGuest | null>(null);
  const [paymentMode, setPaymentMode] = useState<PaymentMode | "">("");
  const [updatingEntryId, setUpdatingEntryId] = useState<string | null>(null);
  const debouncedRegisteredNumber = useDebouncedValue(registeredNumber, 300);

  useEffect(() => {
    setVisibleLogCount(ENTRY_LOG_INITIAL_VISIBLE);
    setOlderCursor(null);
    setEntries([]);
    setPollFrom(undefined);
    setOperatorId("");
    setRegisteredNumber("");
    setGuestName("");
    setSelectedGuest(null);
    setPaymentMode("");
  }, [eventId]);

  const guestSearchQuery = useQuery({
    queryKey: queryKeys.rsvpGuestSearch(eventId, debouncedRegisteredNumber.trim()),
    queryFn: async (): Promise<GateLookupGuest[]> => {
      const term = debouncedRegisteredNumber.trim().toLowerCase();
      const [rsvpRows, walkIns] = await Promise.all([
        rsvpApi.searchGuests(eventId, debouncedRegisteredNumber.trim(), token),
        walkInApi.list(eventId, token),
      ]);

      const fromRsvp: GateLookupGuest[] = rsvpRows
        .filter((g: RsvpGuest) => g.phone.toLowerCase().includes(term))
        .map((g: RsvpGuest) => ({
          id: `rsvp:${g.id}`,
          guestId: g.id,
          name: g.name,
          phone: g.phone,
          email: g.email ?? null,
          party_size: g.party_size,
          tier_counts: g.tier_counts ?? {},
          amount: g.amount ?? g.amount_paid ?? null,
          payment_mode:
            g.payment_mode === "cash" || g.payment_mode === "online" ? g.payment_mode : null,
          source: resolveGuestListLabel(g),
        }));

      const fromWalkIns: GateLookupGuest[] = walkIns
        .filter((g: WalkInGuest) => g.phone.toLowerCase().includes(term))
        .slice(0, 10)
        .map((g: WalkInGuest) => ({
          id: `walkin:${g.id}`,
          guestId: g.id,
          name: g.name,
          phone: g.phone,
          email: g.email ?? null,
          party_size: g.party_size,
          tier_counts: g.tier_counts ?? {},
          amount: g.amount ?? null,
          payment_mode: null,
          source: "Walk-in" as const,
        }));

      const byPhone = new Map<string, GateLookupGuest>();
      [...fromRsvp, ...fromWalkIns].forEach((row) => {
        const key = normalizeGuestNumber(row.phone);
        if (!key) return;
        const existing = byPhone.get(key);
        if (!existing) {
          byPhone.set(key, row);
          return;
        }
        // Prefer RSVP/Guest List over Walk-in when the same number appears in both.
        if (existing.source === "Walk-in" && row.source !== "Walk-in") {
          byPhone.set(key, row);
        }
      });
      return Array.from(byPhone.values()).slice(0, 12);
    },
    enabled: Boolean(eventId && token && debouncedRegisteredNumber.trim().length >= 3 && showGuestResults),
    staleTime: 5000,
  });

  const guestResults: GateLookupGuest[] = guestSearchQuery.data ?? [];

  const handleSelectGuest = (guest: GateLookupGuest) => {
    setSelectedGuest(guest);
    setRegisteredNumber(guest.phone.replace(/\D/g, ""));
    setGuestName(guest.name);
    setShowGuestResults(false);
    setQuantitySold(String(guest.party_size || 1));
    // Pull up the tier they booked so the operator only edits what changed.
    const bookedTier = dominantTierId(guest.tier_counts);
    if (bookedTier && tiers.some((t) => t.tier_id === bookedTier)) {
      setTierId(bookedTier);
    }
    if (guest.amount != null) {
      setAmountOverride(String(guest.amount));
      setAmountTouched(true);
    }
    if (guest.payment_mode === "cash" || guest.payment_mode === "online") {
      setPaymentMode(guest.payment_mode);
    }
  };

  const handleRegisteredNumberChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setRegisteredNumber(digitsOnly);
    setShowGuestResults(true);
    if (selectedGuest && digitsOnly !== selectedGuest.phone.replace(/\D/g, "")) {
      setSelectedGuest(null);
      setGuestName("");
    }
  };

  const listParams = useMemo(
    () =>
      pollFrom
        ? { from: pollFrom, direction: "asc" as const, limit: 200 }
        : { direction: "desc" as const, limit: 50 },
    [pollFrom],
  );

  const entriesQuery = useQuery({
    queryKey: queryKeys.ticketEntries(eventId, listParams),
    queryFn: () => ticketEntriesApi.list(eventId, listParams, token),
    enabled: Boolean(eventId && token),
    refetchInterval: 5000,
  });

  const partnersQuery = useQuery({
    queryKey: queryKeys.ticketingPartners(),
    queryFn: () => ticketingPartnersApi.list(token),
    enabled: Boolean(token),
    staleTime: 60_000,
  });

  const membersQuery = useQuery({
    queryKey: queryKeys.teamMembers(),
    queryFn: () => teamMembersApi.list(token),
    enabled: Boolean(token),
    refetchInterval: 15000,
  });

  useEffect(() => {
    setVisibleLogCount((count) => {
      if (entries.length === 0) return ENTRY_LOG_INITIAL_VISIBLE;
      return Math.min(count, entries.length);
    });
  }, [entries.length]);

  useEffect(() => {
    if (!entriesQuery.data) return;
    if (!pollFrom) {
      setEntries(entriesQuery.data.items);
      setOlderCursor(entriesQuery.data.next_cursor);
      const latest = newestOccurredAt(entriesQuery.data.items);
      if (latest) setPollFrom(latest);
      return;
    }
    if (entriesQuery.data.items.length === 0) return;
    setEntries((prev) => mergeEntries(prev, entriesQuery.data!.items));
    const latest = newestOccurredAt(entriesQuery.data.items);
    if (latest) setPollFrom(latest);
  }, [entriesQuery.data, pollFrom]);

  useEffect(() => {
    if (!tierId && tiers.length > 0) {
      setTierId(tiers[0].tier_id);
    }
  }, [tiers, tierId]);

  // Operators are company-wide, so every operator can work every tier.
  const operatorOptions = useMemo<TeamMember[]>(() => {
    const all = (membersQuery.data ?? []) as TeamMember[];
    return all.filter((m: TeamMember) => String(m.role) === "operator");
  }, [membersQuery.data]);

  useEffect(() => {
    // Only sync once we have actual member data.
    if (!membersQuery.data) return;
    if (operatorOptions.length === 0) {
      setOperatorId("");
      return;
    }
    if (!operatorId || !operatorOptions.some((m) => m.id === operatorId)) {
      setOperatorId(operatorOptions[0].id);
    }
  }, [membersQuery.data, operatorOptions, operatorId]);

  // Default ticketing partner to Zoniq (hidden from Live Ops UI).
  useEffect(() => {
    if (partnerSelection) return;
    const data: TicketingPartner[] | undefined = partnersQuery.data;
    if (!data || data.length === 0) return;
    const zoniq = data.find((p: TicketingPartner) => p.slug === DEFAULT_PARTNER_SLUG);
    setPartnerSelection((zoniq ?? data[0]).id);
  }, [partnersQuery.data, partnerSelection]);

  const selectedTier = useMemo(
    () => tiers.find((t) => t.tier_id === tierId) ?? null,
    [tiers, tierId],
  );

  const parsedQuantity = useMemo(() => {
    const n = Number(quantitySold);
    return Number.isFinite(n) ? Math.floor(n) : NaN;
  }, [quantitySold]);

  const autoAmount = useMemo(() => {
    if (!selectedTier) return 0;
    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) return 0;
    return roundCurrency(Number(selectedTier.price) * parsedQuantity);
  }, [selectedTier, parsedQuantity]);

  const parsedOverride = useMemo(() => {
    if (!amountOverride.trim()) return null;
    const n = Number(amountOverride);
    return Number.isFinite(n) ? n : null;
  }, [amountOverride]);

  const isAmountOverridden = amountTouched;
  const submittableAmount = isAmountOverridden ? (parsedOverride as number) : autoAmount;

  const handleAmountChange = (next: string) => {
    setAmountTouched(true);
    setAmountOverride(next);
  };

  const handleTierChange = (nextTierId: string) => {
    setTierId(nextTierId);
    // Switching tier invalidates any prior manual override (different price basis).
    setAmountOverride("");
    setAmountTouched(false);
  };

  const handleQuantityChange = (next: string) => {
    setQuantitySold(next);
  };

  const handleResetAmount = () => {
    setAmountOverride("");
    setAmountTouched(false);
  };

  const submitDisabled =
    !tierId ||
    !partnerSelection ||
    !operatorId ||
    !Number.isFinite(parsedQuantity) ||
    parsedQuantity < 1 ||
    !Number.isFinite(submittableAmount) ||
    submittableAmount < 0;

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!tierId) throw new Error("Select a ticket tier.");
      if (!operatorId) throw new Error("Choose an operator.");
      if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
        throw new Error("Quantity sold must be at least 1.");
      }
      if (!partnerSelection) throw new Error("Ticketing partner is not ready yet.");
      if (!Number.isFinite(submittableAmount) || submittableAmount < 0) {
        throw new Error("Enter a valid amount.");
      }
      return ticketEntriesApi.create(
        eventId,
        {
          tier_id: tierId,
          operator_id: operatorId,
          quantity_sold: parsedQuantity,
          entry_cost: roundCurrency(submittableAmount),
          ticketing_partner_id: partnerSelection,
          // RSVP, Guest List and Walk-in guests all live in rsvp_guests, so a
          // match links and updates the row whichever list they came from.
          rsvp_guest_id: selectedGuest?.guestId,
          // Unmatched number → the backend creates a walk-in row from this entry.
          guest_phone: registeredNumber.trim() ? toE164(registeredNumber) ?? registeredNumber.trim() : undefined,
          guest_name: guestName.trim() || undefined,
          guest_email: selectedGuest?.email || undefined,
          payment_mode: paymentMode || undefined,
          notes: notes.trim() || undefined,
        },
        token,
      );
    },
    onSuccess: (created) => {
      toast.success("Entry logged");
      setNotes("");
      setQuantitySold("1");
      setAmountOverride("");
      setAmountTouched(false);
      setRegisteredNumber("");
      setGuestName("");
      setSelectedGuest(null);
      setPaymentMode("");
      void queryClient.invalidateQueries({ queryKey: ["rsvp"] });
      setEntries((prev) => mergeEntries(prev, [created]));
      setPollFrom((prev) => {
        if (!prev) return created.occurred_at;
        return new Date(created.occurred_at) > new Date(prev) ? created.occurred_at : prev;
      });
      void queryClient.invalidateQueries({ queryKey: ["ticketEntries", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["eventOs"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not log entry.");
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({
      entryId,
      payload,
    }: {
      entryId: string;
      payload: { entry_cost?: number; payment_mode?: PaymentMode };
    }) => {
      setUpdatingEntryId(entryId);
      return ticketEntriesApi.update(eventId, entryId, payload, token);
    },
    onSuccess: (updated) => {
      toast.success("Entry updated");
      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      void queryClient.invalidateQueries({ queryKey: ["rsvp"] });
      void queryClient.invalidateQueries({ queryKey: ["eventOs"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not update entry.");
    },
    onSettled: () => {
      setUpdatingEntryId(null);
    },
  });

  const totalCount = entriesQuery.data?.total_count ?? entries.length;

  const loadMoreMutation = useMutation({
    mutationFn: async () => {
      if (!olderCursor) {
        throw new Error("No more entries to load.");
      }
      return ticketEntriesApi.list(
        eventId,
        { direction: "desc", limit: ENTRY_LOG_PAGE_SIZE, cursor: olderCursor },
        token,
      );
    },
    onSuccess: (page) => {
      setEntries((prev) => [...prev, ...page.items]);
      setOlderCursor(page.next_cursor);
      setVisibleLogCount((count) => count + page.items.length);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not load more entries.");
    },
  });

  const handleShowMore = () => {
    const nextVisible = visibleLogCount + ENTRY_LOG_PAGE_SIZE;
    if (nextVisible <= entries.length) {
      setVisibleLogCount(nextVisible);
      return;
    }
    loadMoreMutation.mutate();
  };

  const handleShowAllLoaded = () => {
    setVisibleLogCount(entries.length);
  };

  const handleCollapse = () => {
    setVisibleLogCount(ENTRY_LOG_INITIAL_VISIBLE);
  };

  const exportCsvMutation = useMutation({
    mutationFn: () => fetchAllTicketEntries(eventId, token),
    onSuccess: (allEntries) => {
      if (allEntries.length === 0) {
        toast.error("No entries to export.");
        return;
      }
      downloadTicketEntriesCsv(allEntries, ticketEntryExportFilename(eventId));
      toast.success(`Exported ${allEntries.length} entries to CSV`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not export entries.");
    },
  });

  const operatorLabel = (entry: TicketEntryLog) =>
    entry.operator_name || entry.operator_email || "Unknown operator";

  return (
    <div className="eventos-card">
      <div className="eventos-label">Tier entry log</div>
      <p className="eventos-subtle">
        Log door admissions as they happen. New entries appear at the top automatically.
      </p>

      {tiers.length === 0 ? (
        <div className="eventos-empty">Add ticket tiers on the event to log entries.</div>
      ) : (
        <>
          {!membersQuery.isPending && operatorOptions.length === 0 ? (
            <div className="app-shell-query-error" role="alert">
              <div>
                <div className="app-shell-query-error-title">No operators available</div>
                <p className="app-shell-query-error-msg">
                  Add at least one team member with the "operator" role in Profile &amp; Settings before you can
                  log entries.
                </p>
              </div>
            </div>
          ) : null}
          <form
          className="eventos-form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div className="eventos-form-grid">
            <label className="eventos-field" style={{ position: "relative" }}>
              <span className="eventos-field-label">Mobile number</span>
              <input
                className="eventos-input"
                value={registeredNumber}
                onChange={(e) => handleRegisteredNumberChange(e.target.value)}
                onFocus={() => setShowGuestResults(true)}
                onBlur={() => setTimeout(() => setShowGuestResults(false), 150)}
                placeholder="+91 98765 43210"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel"
              />
              {selectedGuest ? (
                <span className="eventos-subtle">
                  Matched {selectedGuest.source}: {selectedGuest.name} · party of {selectedGuest.party_size}
                </span>
              ) : null}
              {showGuestResults && debouncedRegisteredNumber.trim().length >= 3 ? (
                <div
                  className="eventos-card"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    maxHeight: 220,
                    overflowY: "auto",
                    padding: 4,
                  }}
                >
                  {guestSearchQuery.isFetching ? (
                    <div className="eventos-empty">Searching…</div>
                  ) : guestResults.length === 0 ? (
                    <div className="eventos-empty">No matching guests in RSVP, Guest List, or Walk-ins.</div>
                  ) : (
                    guestResults.map((guest) => (
                      <button
                        type="button"
                        key={guest.id}
                        className="eventos-nav-btn"
                        style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 4 }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectGuest(guest);
                        }}
                      >
                        [{guest.source}] {guest.name} · {guest.phone} · party of {guest.party_size}
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </label>
            {!selectedGuest && registeredNumber.trim() ? (
              <label className="eventos-field">
                <span className="eventos-field-label">Guest name</span>
                <input
                  className="eventos-input"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Walk-in guest"
                />
                <span className="eventos-subtle">
                  No match for this number — logging will add them as a walk-in.
                </span>
              </label>
            ) : null}
            <label className="eventos-field">
              <span className="eventos-field-label">Choose operator</span>
              <MobileSelect
                label="Operator"
                className="eventos-select"
                value={operatorId}
                onChange={setOperatorId}
                disabled={membersQuery.isPending || operatorOptions.length === 0}
                options={
                  membersQuery.isPending
                    ? [{ value: "", label: "Loading operators…" }]
                    : operatorOptions.length === 0
                      ? [{ value: "", label: "No operators available" }]
                      : [
                          { value: "", label: "Select operator", disabled: true },
                          ...operatorOptions.map((m) => ({
                            value: m.id,
                            label: `${m.name}${m.email ? ` · ${m.email}` : ""}`,
                          })),
                        ]
                }
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Quantity sold</span>
              <input
                className="eventos-input"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={quantitySold}
                onChange={(e) => handleQuantityChange(e.target.value)}
                required
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Tier</span>
              <MobileSelect
                label="Tier"
                className="eventos-select"
                value={tierId}
                onChange={handleTierChange}
                options={tiers.map((tier) => ({
                  value: tier.tier_id,
                  label: `${tier.name} (${formatINR(tier.price)})`,
                }))}
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">
                Amount{" "}
                <span className="eventos-subtle" style={{ fontWeight: 400 }}>
                  (auto: {formatINR(autoAmount)})
                </span>
              </span>
              <CurrencyInput
                step="any"
                value={isAmountOverridden ? amountOverride : String(autoAmount || "")}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder={String(autoAmount)}
              />
              {isAmountOverridden ? (
                <button
                  type="button"
                  className="eventos-link-btn"
                  onClick={handleResetAmount}
                  style={{ alignSelf: "flex-start", marginTop: 4 }}
                >
                  Reset to auto
                </button>
              ) : null}
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Mode of payment</span>
              <MobileSelect
                label="Payment mode"
                className="eventos-select"
                value={paymentMode}
                onChange={(v) => setPaymentMode(v as PaymentMode | "")}
                options={[
                  { value: "", label: "Select mode" },
                  { value: "cash", label: "Cash" },
                  { value: "online", label: "Online" },
                ]}
              />
            </label>
            <label className="eventos-field" style={{ gridColumn: "1 / -1" }}>
              <span className="eventos-field-label">Notes (optional)</span>
              <input
                className="eventos-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Walk-in, paid cash"
              />
            </label>
          </div>
          <button
            type="submit"
            className="eventos-primary-btn"
            disabled={submitDisabled || createMutation.isPending}
          >
            {createMutation.isPending ? "Logging…" : "Log entry"}
          </button>
          </form>
        </>
      )}

      <div className="live-ops-entry-history">
        {entriesQuery.isError ? (
          <QueryErrorBanner
            message={entriesQuery.error instanceof Error ? entriesQuery.error.message : "Could not load entries."}
            onRetry={() => void entriesQuery.refetch()}
            retrying={entriesQuery.isFetching}
          />
        ) : null}

        {entriesQuery.isPending && entries.length === 0 ? (
          <div className="eventos-empty">Loading entries…</div>
        ) : entries.length === 0 ? (
          <div className="eventos-empty">Entries will appear here once staff log admissions at the door.</div>
        ) : (
          <TicketEntryLogList
            entries={entries}
            visibleCount={visibleLogCount}
            totalCount={totalCount}
            olderCursor={olderCursor}
            loadingMore={loadMoreMutation.isPending}
            onShowMore={handleShowMore}
            onShowAllLoaded={handleShowAllLoaded}
            onCollapse={handleCollapse}
            onExportCsv={() => exportCsvMutation.mutate()}
            exportingCsv={exportCsvMutation.isPending}
            operatorLabel={operatorLabel}
            onUpdateEntry={(entryId, payload) => updateEntryMutation.mutate({ entryId, payload })}
            updatingEntryId={updatingEntryId}
          />
        )}
      </div>
    </div>
  );
};
